import _extends from '@babel/runtime/helpers/extends';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import { forwardRef, useRadioGroupContext, useMultiStyleConfig, omitThemingProps, useRadio, layoutPropNames, chakra } from '@chakra-ui/react';
import { callAll, split } from '@chakra-ui/utils';
import * as React from 'react';
import { Check } from '../../icons/Check.js';
var _excluded = ["spacing", "children", "isDisabled", "isFocusable", "inputProps"];
function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}

/**
 * Radio component is used in forms when a user needs to select a single value from
 * several options.
 *
 * @see Docs https://chakra-ui.com/radio
 */
var Radio = forwardRef(function (props, ref) {
  var _props$name;
  var onChangeProp = props.onChange,
    valueProp = props.value;
  var group = useRadioGroupContext();
  var styles = useMultiStyleConfig('Radio', _objectSpread(_objectSpread({}, group), props));
  var ownProps = omitThemingProps(props);
  var _ownProps$spacing = ownProps.spacing,
    spacing = _ownProps$spacing === void 0 ? '0.5rem' : _ownProps$spacing,
    children = ownProps.children,
    _ownProps$isDisabled = ownProps.isDisabled,
    isDisabled = _ownProps$isDisabled === void 0 ? group === null || group === void 0 ? void 0 : group.isDisabled : _ownProps$isDisabled,
    _ownProps$isFocusable = ownProps.isFocusable,
    isFocusable = _ownProps$isFocusable === void 0 ? group === null || group === void 0 ? void 0 : group.isFocusable : _ownProps$isFocusable,
    htmlInputProps = ownProps.inputProps,
    rest = _objectWithoutProperties(ownProps, _excluded);
  var isChecked = props.isChecked;
  if ((group === null || group === void 0 ? void 0 : group.value) != null && valueProp != null) {
    isChecked = group.value === valueProp;
  }
  var onChange = onChangeProp;
  if (group !== null && group !== void 0 && group.onChange && valueProp != null) {
    onChange = callAll(group.onChange, onChangeProp);
  }
  var name = (_props$name = props === null || props === void 0 ? void 0 : props.name) !== null && _props$name !== void 0 ? _props$name : group === null || group === void 0 ? void 0 : group.name;
  var _useRadio = useRadio(_objectSpread(_objectSpread({}, rest), {}, {
      isChecked: isChecked,
      isFocusable: isFocusable,
      isDisabled: isDisabled,
      onChange: onChange,
      name: name
    })),
    getInputProps = _useRadio.getInputProps,
    getCheckboxProps = _useRadio.getCheckboxProps,
    getLabelProps = _useRadio.getLabelProps,
    htmlProps = _useRadio.htmlProps,
    getRootProps = _useRadio.getRootProps,
    state = _useRadio.state;
  var _split = split(htmlProps, layoutPropNames),
    _split2 = _slicedToArray(_split, 2),
    layoutProps = _split2[0],
    otherProps = _split2[1];
  var checkboxProps = getCheckboxProps(otherProps);
  var inputProps = getInputProps(htmlInputProps, ref);
  var labelProps = getLabelProps();
  var rootProps = Object.assign({}, layoutProps, getRootProps());
  var rootStyles = _objectSpread({
    display: 'inline-flex',
    alignItems: 'center',
    verticalAlign: 'top',
    cursor: 'pointer'
  }, styles.container);
  var checkboxStyles = _objectSpread({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  }, styles.control);
  var labelStyles = _objectSpread({
    userSelect: 'none',
    marginStart: spacing
  }, styles.label);
  var iconStyles = React.useMemo(function () {
    return _objectSpread({
      opacity: state.isChecked ? 1 : 0,
      transform: state.isChecked ? 'scale(1)' : 'scale(0.95)'
    }, styles.icon);
  }, [state.isChecked, styles.icon]);
  return /*#__PURE__*/React.createElement(chakra.label, _extends({
    className: "chakra-radio"
  }, rootProps, {
    __css: rootStyles
  }), /*#__PURE__*/React.createElement("input", _extends({
    className: "chakra-radio__input"
  }, inputProps)), /*#__PURE__*/React.createElement(chakra.span, _extends({
    className: "chakra-radio__control"
  }, checkboxProps, {
    __css: checkboxStyles
  }), /*#__PURE__*/React.createElement(Check, {
    __css: iconStyles
  })), children && /*#__PURE__*/React.createElement(chakra.span, _extends({
    className: "chakra-radio__label"
  }, labelProps, {
    __css: labelStyles
  }), children));
});
export { Radio };